import _ from 'lodash';
import SAlert from 'react-s-alert';
import * as queryString from 'query-string';
import { useState, useCallback } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
// types
import { IProjectJson } from '@scale/shared/types/project';
import { useConfigAttributes, ConfigAttributes } from './useConfigAttributes';

export interface IConfigMeta {
  type: string;
  id?: string;
  taskType?: string;
}

const canSubmitConfigChanges = (
  configs: Record<string, any>,
  changes: Record<string, any>,
  parseJSON = true,
): boolean => {
  return _.every(
    _.mapValues(changes, (value, path) => {
      const { _type, children } = _.get(configs, path);
      if (_type === 'Array' && !!children) {
        return Object.values(value).map((item: any) => {
          Object.keys(item).map((key: string) => {
            if (key === '_id') {
              return;
            }
            if (
              children[key] &&
              ['Mixed', 'Array'].includes(children[key]._type) &&
              typeof item[key] === 'string'
            ) {
              try {
                item[key] = JSON.parse(item[key]);
              } catch (err) {
                SAlert.error(`Expected path ${key} to be JSON parsable, got ${item[key]}`, {
                  timeout: 'none',
                });
                return false;
              }
            }
          });
        });
      } else if (_type === 'Mixed' || _type === 'Array') {
        // Simple arrays can be parsed as JSON like mixed types
        try {
          changes[path] = parseJSON ? JSON.parse(value) : value;
        } catch (err) {
          SAlert.error(`Expected path ${path} to be JSON parsable, got ${value}`, {
            timeout: 'none',
          });
          return false;
        }
      }
      return true;
    }),
  );
};

export const getConfigQuery = (
  configMeta: IConfigMeta,
  configAttributes: ConfigAttributes,
): Record<string, any> => {
  const query = { type: configMeta.type, ...configAttributes };
  return query;
};

export const fetchConfig = async (configMeta: IConfigMeta, configAttributes: ConfigAttributes) => {
  const query = getConfigQuery(configMeta, configAttributes);
  // FIXME (copied from ConfigDashboard): Fetch route includes undefined for projects: /corp-api/lookup/undefined/config
  return await apiFetch<{ config: Record<string, any>; version: number; allMeta: any }>(
    null,
    `corp-api/lookup/${configMeta.id}/config?${queryString.stringify(query)}`,
    { method: 'GET' },
  );
};

interface ConfigDataHook {
  trySaveChangesAsync(parseJSON?: boolean): Promise<void>;
  trySaveChanges(parseJSON?: boolean): void;
  newSetValue(path: string, newValue: any): void;
  resetConfig(): void;
  loading: boolean;
  saving: boolean;
  configs: Record<string, any> | null;
  triggerGetConfigs(): void;
  changes: Record<string, any>;
  ogConfigs: Record<string, any> | null;
}

export const useConfigData = (
  userEmail: string | undefined, // XXX server can already infer performedBy from request user
  type: string,
  configMeta: IConfigMeta,
  project: IProjectJson | null,
): ConfigDataHook => {
  // state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<Record<string, any> | null>(null);
  const [ogConfigs, setOgConfigs] = useState<Record<string, any> | null>(null);
  const [versionState, setVersion] = useState(-1);
  const [changes, setChanges] = useState<Record<string, any>>({});

  const configAttributes = useConfigAttributes(configMeta, project);

  const loadComponent = useCallback(async () => {
    if (!configMeta || !configAttributes) {
      return;
    }
    try {
      const { config, version } = await fetchConfig(configMeta, configAttributes);
      if (_.isNil(config) || _.isNil(version)) {
        return;
      }
      setConfigs(config);
      setOgConfigs(_.cloneDeep(config));
      setVersion(version);
      setLoading(false);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  }, [configAttributes, configMeta]);

  const triggerGetConfigs = useCallback(async () => {
    await loadComponent();
  }, [loadComponent]);

  const newSetValue = (path: string, newValue: any): void => {
    // no-op if configs are missing
    if (!configs || !ogConfigs) {
      return;
    }

    let newConfigs = _.cloneDeep(configs);
    const fullPath = path + '.value';
    // set configs
    newConfigs = _.set(newConfigs, fullPath, newValue);
    setConfigs(newConfigs);

    const ogValue = _.get(ogConfigs, fullPath);
    const sameValue = JSON.stringify(ogValue) === JSON.stringify(newValue);
    const emptyValue = _.isNil(ogValue) && (newValue === '' || newValue === false);

    if (sameValue || emptyValue) {
      // remove the change if the change gets reverted to the original value
      setChanges(_.omit(changes, [path]));
    } else {
      // record the change
      setChanges({ ...changes, [path]: newValue });
    }
  };

  // save configs
  const saveChanges = async () => {
    if (!configMeta || !configAttributes) {
      return;
    }

    setSaving(true);
    const query = getConfigQuery(configMeta, configAttributes);
    const body = {
      changes,
      query,
      lastVersion: versionState,
      user: userEmail,
      type,
      attributes: configAttributes,
    };

    // set saving
    try {
      const { config, version } = await apiFetch(null, `corp-api/lookup/${configMeta.id}/config`, {
        method: 'POST',
        body,
      });

      setConfigs(config);
      setOgConfigs(_.cloneDeep(config));
      setVersion(version);
      setChanges({});
      setSaving(false);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setSaving(false);
    }
  };

  const resetConfig = () => {
    setConfigs(_.cloneDeep(ogConfigs));
    setChanges({});
  };

  // ensure changes are valid, e.g. valid JSON for Mixed configs
  // mostly copied from current implementation
  const trySaveChanges = (parseJSON = true) => {
    if (configs && changes && canSubmitConfigChanges(configs, changes, parseJSON)) {
      saveChanges();
    }
  };

  const trySaveChangesAsync = async (parseJSON = true) => {
    if (configs && changes && canSubmitConfigChanges(configs, changes, parseJSON)) {
      await saveChanges();
    }

    return;
  };

  return {
    ogConfigs,
    trySaveChanges,
    trySaveChangesAsync,
    newSetValue,
    resetConfig,
    loading,
    saving,
    configs,
    triggerGetConfigs,
    changes,
  };
};
